import { Stack, Text, Card, Group, ActionIcon, Loader, Alert, Pagination, Center, ScrollArea, Box } from '@mantine/core'
import { IconMapPin, IconCalendar, IconTrash, IconEdit, IconUser } from '@tabler/icons-react'
import { useState, useEffect, useRef } from 'react'
import { useSpottingsInBounds, useDeleteSpotting } from '../../hooks/useSpottings'
import type { Spotting } from '../../types/api'
import CategoryDisplay from './CategoryDisplay'
import { CategoryFilter } from './CategoryFilter'

interface SpottingsListProps {
    onSpottingClick?: (spotting: Spotting, scrollPosition?: number) => void
    onEditSpotting?: (spotting: Spotting) => void
    mapBounds?: {
        north: number
        south: number
        east: number
        west: number
    } | null
    savedScrollPosition?: number
    onScrollPositionChange?: (position: number) => void
    selectedCategoryIds: string[]
    onCategoryChange: (categoryIds: string[]) => void
    isMobile?: boolean
}

export function SpottingsList({ onSpottingClick, onEditSpotting, mapBounds, savedScrollPosition, onScrollPositionChange, selectedCategoryIds, onCategoryChange, isMobile }: SpottingsListProps) {
    const [currentPage, setCurrentPage] = useState(1)
    const pageSize = 100 // Use maximum API limit
    const scrollAreaRef = useRef<HTMLDivElement>(null)

    // Fetch spottings using React Query with bounds filtering and pagination
    const { data: spottingsResponse, isLoading, error, refetch } = useSpottingsInBounds(
        mapBounds || null,
        {
            page: currentPage,
            limit: pageSize,
            enabled: !!mapBounds, // Only fetch when we have bounds
            categoryIds: selectedCategoryIds, // Add category filtering
        }
    )

    // Restore scroll position when component mounts or savedScrollPosition changes
    useEffect(() => {
        if (savedScrollPosition && scrollAreaRef.current) {
            const scrollElement = scrollAreaRef.current.querySelector('[data-scrollarea-viewport]')
            if (scrollElement) {
                scrollElement.scrollTop = savedScrollPosition
            }
        }
    }, [savedScrollPosition])

    // Save scroll position periodically
    useEffect(() => {
        const scrollElement = scrollAreaRef.current?.querySelector('[data-scrollarea-viewport]')
        if (!scrollElement || !onScrollPositionChange) return

        const handleScroll = () => {
            onScrollPositionChange(scrollElement.scrollTop)
        }

        scrollElement.addEventListener('scroll', handleScroll, { passive: true })
        return () => scrollElement.removeEventListener('scroll', handleScroll)
    }, [onScrollPositionChange])

    const getCurrentScrollPosition = () => {
        const scrollElement = scrollAreaRef.current?.querySelector('[data-scrollarea-viewport]')
        return scrollElement ? scrollElement.scrollTop : 0
    }

    // Delete mutation
    const deleteMutation = useDeleteSpotting()

    const handleDelete = async (id: string, event: React.MouseEvent) => {
        event.stopPropagation() // Prevent triggering onSpottingClick

        if (window.confirm('Are you sure you want to delete this spotting?')) {
            try {
                await deleteMutation.mutateAsync(id)
                // If we deleted the last item on the current page and it's not page 1, go back a page
                if (spottingsResponse?.data.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1)
                }
            } catch (error) {
                console.error('Failed to delete spotting:', error)
                // You could show a toast notification here
            }
        }
    }

    const handleEdit = (spotting: Spotting, event: React.MouseEvent) => {
        event.stopPropagation() // Prevent triggering onSpottingClick
        onEditSpotting?.(spotting)
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    // Loading state
    if (isLoading) {
        return (
            <Stack align="center" justify="center" className="flex-1 py-8">
                <Loader size="md" />
                <Text size="sm" c="dimmed">Loading spottings in view...</Text>
            </Stack>
        )
    }

    // Error state
    if (error) {
        return (
            <Alert color="red" title="Error loading spottings" className="mb-4">
                <Text size="sm">
                    {error instanceof Error ? error.message : 'Failed to load spottings'}
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                    <button
                        onClick={() => refetch()}
                        className="text-blue-500 underline touch-manipulation"
                        style={{ touchAction: 'manipulation' }}
                    >
                        Try again
                    </button>
                </Text>
            </Alert>
        )
    }

    // No bounds yet (map still loading)
    if (!mapBounds) {
        return (
            <Stack align="center" justify="center" className="flex-1 py-8">
                <Text size="sm" c="dimmed">Loading map...</Text>
            </Stack>
        )
    }

    const spottings = spottingsResponse?.data || []
    const totalPages = spottingsResponse?.meta?.totalPages || 0
    const totalCount = spottingsResponse?.meta?.total || 0

    // Always show the main container with filter
    return (
        <ScrollArea.Autosize className="gap-4" ref={scrollAreaRef}>
            <Group justify="space-between" align="center" wrap="nowrap" gap="md">
                <Text size="md" fw={600} className="flex-1 min-w-0">
                    Spottings in View ({totalCount})
                </Text>
                {totalPages > 1 && (
                    <Text size="xs" c="dimmed" className="flex-shrink-0">
                        Page {currentPage} of {totalPages}
                    </Text>
                )}
            </Group>

            {/* Category Filter - Always visible */}
            <div style={{ marginBottom: '1rem' }}>
                <CategoryFilter
                    selectedCategoryIds={selectedCategoryIds}
                    onCategoryChange={onCategoryChange}
                    isMobile={isMobile}
                />
            </div>

            {/* Content based on state */}
            {spottings.length === 0 && currentPage === 1 ? (
                // Empty state
                <Box
                    className="flex-1 rounded border-2 border-dashed flex items-center justify-center p-8"
                    style={{
                        backgroundColor: 'var(--mantine-color-default)',
                        borderColor: 'var(--mantine-color-default-border)',
                        color: 'var(--mantine-color-dimmed)',
                    }}
                >
                    <Stack align="center" gap="sm">
                        <IconMapPin size={48} stroke={1} />
                        <Text size="lg" fw={500}>No spottings in view</Text>
                        <Text size="sm" ta="center">
                            Move the map around or zoom out to see more spottings, or add your first one!
                        </Text>
                    </Stack>
                </Box>
            ) : (
                // Results content
                <>
                    {/* Pagination - Top */}
                    {totalPages > 1 && (
                        <Center>
                            <Pagination
                                value={currentPage}
                                onChange={setCurrentPage}
                                total={totalPages}
                                size="xs"
                                withEdges
                            />
                        </Center>
                    )}

                    {spottings.map((spotting) => (
                        <Card
                            key={spotting.id}
                            shadow="sm"
                            padding="sm"
                            radius="md"
                            withBorder
                            className="active:shadow-lg transition-shadow touch-manipulation mt-5"
                            style={{ cursor: 'default' }}
                            onTouchStart={() => { }} // Enable :active pseudo-class on iOS
                        >
                            <Stack gap="xs">
                                {/* Title and Actions Row */}
                                <Group justify="space-between" align="flex-start" wrap="nowrap">
                                    {/* Thumbnail Image */}
                                    {spotting.imageUrl && (
                                        <div className="flex-shrink-0 mr-3">
                                            <img
                                                src={spotting.imageUrl}
                                                alt="Spotting thumbnail"
                                                className="w-16 h-16 object-cover rounded-md border border-gray-200 dark:border-gray-600"
                                                style={{ touchAction: 'manipulation' }}
                                                onClick={() => onSpottingClick?.(spotting, getCurrentScrollPosition())}
                                            />
                                        </div>
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <Text
                                            fw={500}
                                            size="sm"
                                            lineClamp={1}
                                            c="blue"
                                            className="break-words cursor-pointer hover:underline transition-all duration-200"
                                            onClick={() => onSpottingClick?.(spotting, getCurrentScrollPosition())}
                                            style={{
                                                touchAction: 'manipulation',
                                                textDecoration: 'none'
                                            }}
                                            title="Click to view details"
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.textDecoration = 'underline'
                                                e.currentTarget.style.color = 'var(--mantine-color-blue-7)'
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.textDecoration = 'none'
                                                e.currentTarget.style.color = 'var(--mantine-color-blue-6)'
                                            }}
                                        >
                                            <CategoryDisplay categoryId={spotting.categoryId} />
                                        </Text>
                                        {spotting.description && (
                                            <Text size="xs" c="dimmed" lineClamp={2} mt={2} className="break-words">
                                                {spotting.description}
                                            </Text>
                                        )}
                                        {/* Creator information */}
                                        <Group gap={4} mt={4}>
                                            <IconUser size={12} />
                                            <Text size="xs" c="dimmed">
                                                by {spotting.creator.username}
                                            </Text>
                                        </Group>
                                    </div>

                                    <Group gap="xs" className="flex-shrink-0">
                                        <ActionIcon
                                            variant="subtle"
                                            color="blue"
                                            size="sm"
                                            onClick={(e) => handleEdit(spotting, e)}
                                            loading={deleteMutation.isPending}
                                            style={{ touchAction: 'manipulation' }}
                                        >
                                            <IconEdit size={12} />
                                        </ActionIcon>
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            size="sm"
                                            onClick={(e) => handleDelete(spotting.id, e)}
                                            loading={deleteMutation.isPending}
                                            style={{ touchAction: 'manipulation' }}
                                        >
                                            <IconTrash size={12} />
                                        </ActionIcon>
                                    </Group>
                                </Group>

                                {/* Address/Coordinates and Date Row - Always visible */}
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                                    <Group justify="space-between" align="center" wrap="nowrap">
                                        <Text size="xs" c="blue" fw={500} className="flex-1 min-w-0">
                                            📍 {spotting.address || `${spotting.latitude.toFixed(3)}, ${spotting.longitude.toFixed(3)}`}
                                        </Text>
                                        <Group gap={4} className="flex-shrink-0">
                                            <IconCalendar size={10} />
                                            <Text size="xs" c="dimmed" className="whitespace-nowrap">
                                                {formatDate(spotting.createdAt)}
                                            </Text>
                                        </Group>
                                    </Group>
                                </div>
                            </Stack>
                        </Card>
                    ))}

                    {/* Pagination - Bottom */}
                    {totalPages > 1 && (
                        <Center mt="md">
                            <Pagination
                                value={currentPage}
                                onChange={setCurrentPage}
                                total={totalPages}
                                size="xs"
                                withEdges
                            />
                        </Center>
                    )}
                </>
            )}
        </ScrollArea.Autosize>

    )
} 
